import { Alert } from '@mm/components';
import { ExclamationIcon } from '@mm/icons';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import {
  activeChatbotIdSelector,
  activeChatbotSelector,
} from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useContextSelector } from 'use-context-selector';
import useRouter from 'use-react-router';
import SelectorWithArrow from '../../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { extraPauseInterval } from '../../../../liveChat/components/MessageSender/MuteBotSelector';
import { LiveChatContext } from '../../../../liveChat/LiveChatContext';
import SettingSection from '../../../components/SettingSection/SettingSection';
import './InboxSettingsSection.pcss';

const items = extraPauseInterval.map(i => ({
  id: i.duration,
  name: i.text.replace('+', ''),
}));

export const InboxSettingsSection = () => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const chatbot = useSelector(activeChatbotSelector);
  const { history } = useRouter();
  const chatbotCRUDActions = useChatbotCRUDActions();
  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef?.current,
  );

  const [selectedItem, setSelectedItem] = useState(
    items.find(i => i.id * 60 === chatbot?.messagesSettings?.liveChatTimeout),
  );

  useEffect(() => {
    if (!selectedItem) {
      setSelectedItem(
        items.find(
          i => i.id * 60 === chatbot?.messagesSettings?.liveChatTimeout,
        ),
      );
    }
  }, [chatbot?.messagesSettings?.liveChatTimeout]);

  const onSelectChange = async (id: number | string) => {
    const newValue = Number(id);
    if (newValue !== selectedItem?.id) {
      setSelectedItem(items.find(i => i.id === Number(id)));
      await chatbotCRUDActions.update({
        ...chatbot,
        // @ts-ignore
        messagesSettings: {
          ...chatbot.messagesSettings,
          liveChatTimeout: newValue * 60,
        },
      });
      // Note: We cash pause value in Node not to request it too frqeuently from PG for each user.
      // If we change pause we need to re-set the value and the easiest way is to trigger LC reset.
      lcService?.send('LC_VIEW_BULK_RESET');
    }
  };

  return (
    <div className="inbox-settings-section">
      <Alert type="info" showInfoIcon showCloseIcon={false}>
        Looking for Business Hours & Away Settings? We moved it to{' '}
        <span
          className="inbox-settings-section__link"
          onClick={() =>
            history.push(
              BotBuilderRoutes.automationRules.getLink({
                chatbotId,
                kind: 'business_hours',
              }),
            )
          }
        >
          Automation Rules.
        </span>
      </Alert>
      <SettingSection title="Inbox Settings">
        <div className="inbox-settings__text">
          <div className="inbox-settings__question-row">
            How long do you want to pause the bot?
          </div>
          <div className="inbox-settings__clarification-row">
            If the agent starts to respond manually
          </div>
        </div>
        <div className="inbox-settings__selection-row">
          <div className="inbox-settings__selection-text">
            Pause Chatbot for:
          </div>
          <SelectorWithArrow
            onChange={onSelectChange}
            items={items}
            selectedItem={selectedItem}
            className="inbox-settings__selector"
          />
          {Number(selectedItem?.id) * 60 < 61 && (
            <div className="inbox-settings__selection-warning">
              <ExclamationIcon color="#ff344a" />
              <div className="inbox-settings__selection-warning--text">
                Not Recommended. Chatbot may interrupt your conversation
              </div>
            </div>
          )}
        </div>
      </SettingSection>
    </div>
  );
};
